<template>
  <div class="conversation-history" :style="$attrs.style">
    <div v-if="conversations.length === 0" class="empty-state">
      <span>אין שיחות קודמות</span>
    </div>
    <template v-else>
      <div
        v-for="conversation in orderedConversations"
        :key="conversation.conversation_id"
        class="conversation-item"
        :class="{ 'conversation-item--team': conversation.type === 'intercom' || isEscalatedOpen(conversation) }"
        @click="$emit('select', conversation)"
      >
        <span
          class="conversation-title"
          :class="{ 'conversation-title--team': conversation.type === 'intercom' }"
        >
          {{ conversation.type === 'intercom' ? 'שיחה עם הצוות' : conversation.title }}
        </span>
        <div class="conversation-item__actions">
          <span v-if="conversation.type === 'intercom' || isEscalatedOpen(conversation)" class="return-to-chat">
            <img class="return-to-chat-icon" :src="require('@/assets/chat-bot/arrow-left.svg')" aria-hidden="true" />
            <span class="return-to-chat-text">חזרה לשיחה</span>
          </span>
          <span v-if="conversation.hasUnreadCx" class="unread-badge" aria-label="הודעה שלא נקראה">1</span>
          <img
            v-if="!(conversation.type === 'intercom' || isEscalatedOpen(conversation))"
            class="menu-dots"
            :src="require('@/assets/chat-bot/ellipsis-vertical.svg')"
            @click.stop="$emit('open-menu', conversation)"
          />
        </div>
      </div>
      <div class="history-footer">
        <img :src="require('@/assets/chat-bot/history.svg')" class="history-icon" />
        <span>הצ׳אט שומר את 20 השיחות האחרונות שלך</span>
      </div>
    </template>
  </div>
</template>

<script>
import _ from 'lodash';

export default {
  name: 'ConversationHistory',
  props: {
    conversations: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    // Lift each escalated ("intercom") conversation to sit immediately above the
    // chatbot conversation that triggered it. The backend returns it below; product
    // wants the team conversation to pop out above its originating chat.
    orderedConversations() {
      const [teamConversations, chatbotConversations] = _.partition(
        this.conversations,
        c => c.type === 'intercom',
      );
      const chatbotById = _.keyBy(chatbotConversations, 'conversation_id');
      const teamByParent = _.keyBy(
        _.filter(teamConversations, c => chatbotById[c.cashflowAssistantConversationId]),
        'cashflowAssistantConversationId',
      );
      const ordered = _.flatMap(chatbotConversations, c => {
        const team = teamByParent[c.conversation_id];
        return team ? [team, c] : [c];
      });
      const orphans = _.reject(teamConversations, c => chatbotById[c.cashflowAssistantConversationId]);
      return [...ordered, ...orphans];
    },
  },
  methods: {
    isEscalatedOpen(conversation) {
      return _.get(conversation, 'escalation.status') === 'open';
    },
  },
};
</script>

<style scoped lang="scss">
.conversation-history {
  display: flex;
  flex-direction: column;
  padding: 0;
  overflow-y: auto;
  overflow-x: hidden;
  background: linear-gradient(180deg, #FCF7ED 0%, #F8F1DE 100%);
  box-sizing: border-box;
  touch-action: pan-y;
  overscroll-behavior-x: none;
  width: 100%;
}

.conversation-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 28px 24px;
  cursor: pointer;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  direction: rtl;

  &:hover {
    background-color: rgba(0, 0, 0, 0.03);
  }
}

/* The "return to chat" pill (40px) is taller than a title line, so the default
   28px padding makes this row stand proud of the others. Trim the vertical
   padding so the pill stays vertically centered within the same row height. */
.conversation-item--team {
  padding-top: 20px;
  padding-bottom: 20px;
}

.conversation-title {
  font-size: 18px;
  font-weight: 500;
  color: #202125;
  text-align: right;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-left: 16px;
}

.conversation-item__actions {
  display: flex;
  align-items: center;
  gap: 15px;
  direction: rtl;
  flex-shrink: 0;
}

.unread-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  border-radius: 9px;
  background-color: #F34F8A;
  color: #ffffff;
  font-size: 12px;
  font-weight: 700;
  line-height: 14px;
  flex-shrink: 0;
}

.return-to-chat {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #FFCF03;
  border-radius: 10px;
  padding: 8px 16px;
  // Force LTR so the arrow (first child) sits on the left and the text on the
  // right, matching the Figma design — the parent row is RTL otherwise.
  direction: ltr;
  flex-shrink: 0;
}

.return-to-chat-icon {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
}

.return-to-chat-text {
  font-size: 18px;
  font-weight: 400;
  line-height: 24px;
  color: #202125;
  white-space: nowrap;
}

.menu-dots {
  width: 24px;
  height: 24px;
  opacity: 1;
  flex-shrink: 0;
  cursor: pointer;
}

.history-icon {
  width: 15px;
  height: 15px;
}

.history-footer {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 20px;
  font-size: 13px;
  color: #000000;
  direction: rtl;
  margin-top: auto;
}

.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  font-size: 18px;
  color: #202125;
  opacity: 0.5;
  direction: rtl;
}
</style>
